import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Painekoe as PainekoeRivi } from "./api";
import Liitteet from "./Liitteet";
import { Tilasana } from "./Tila";
import { t, virheTeksti, type KaannosAvain } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Linjan painekoe (D1, Toni 18.9.2026).

   Painekoe oli kolmessa paikassa eikä yksikään niistä ollut koe:
   `linja.painekoe` (vapaa teksti), `linja.tila = 'painekoe_ok'` ja
   `tarkastus.menetelma = 'painekoe'` (0 riviä). Luovutusvalmius luki
   «painekoe ok» siitä, että TEKSTIKENTTÄ EI OLE TYHJÄ — ja pekilossa
   niissä kentissä luki «tulos ei kirjattu».

   Nyt se on tarkastusrivi kuten NDT: menetelmä, päivä, tulos ja
   pöytäkirja liitteenä. Neljäs tulos `ei_tiedossa` on tuonnin arvo:
   lähde ei kertonut tulosta, eikä sitä keksitä.
   ═══════════════════════════════════════════════════════════ */

const TULOKSET: { arvo: PainekoeRivi["tulos"]; nimi: KaannosAvain }[] = [
  { arvo: "kesken",      nimi: "pai.tulos.kesken" },
  { arvo: "hyvaksytty",  nimi: "pai.tulos.hyvaksytty" },
  { arvo: "hylatty",     nimi: "pai.tulos.hylatty" },
  { arvo: "ei_tiedossa", nimi: "pai.tulos.ei_tiedossa" },
];

export function Painekoe(
  { linjaId, saaKirjata, saaPoistaaLiitteen = false }:
  { linjaId: number; saaKirjata: boolean; saaPoistaaLiitteen?: boolean },
) {
  const [rivi, setRivi] = useState<PainekoeRivi | null>(null);
  const [ladattu, setLadattu] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [auki, setAuki] = useState(false);
  const [tulos, setTulos] = useState<PainekoeRivi["tulos"]>("kesken");
  const [paiva, setPaiva] = useState("");
  const [raportti, setRaportti] = useState("");
  const [tarkastaja, setTarkastaja] = useState("");
  /** 161: palvelin vaati perusteen (NDT kesken) — kenttä avautuu vasta silloin. */
  const [perusteKysytty, setPerusteKysytty] = useState(false);
  const [peruste, setPeruste] = useState("");
  /** T396 (Toni 30.9.2026): pöytäkirja valitaan jo kirjauslomakkeessa. Ennen liitteen sai vasta tallennuksen
   *  jälkeen, ja 29.9. mitattiin 0 / 140 painekoetta liitteellä — kenttä jota ei näe ei täyty. */
  const [tiedosto, setTiedosto] = useState<File | null>(null);

  const lataa = useCallback(() => {
    setLadattu(false);
    api.painekoe(linjaId)
      .then(({ painekoe }) => {
        setRivi(painekoe);
        setTulos(painekoe?.tulos ?? "kesken");
        setPaiva(painekoe?.paiva ?? "");
        setRaportti(painekoe?.raportti ?? "");
        setTarkastaja(painekoe?.tarkastaja ?? "");
        setVirhe(null);
      })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pai.hakuvirhe")))
      .finally(() => setLadattu(true));
  }, [linjaId]);

  useEffect(lataa, [lataa]);

  const lueDataUrl = (f: File) => new Promise<string>((res, rej) => {
    const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("luku")); r.readAsDataURL(f);
  });

  async function tallenna() {
    try {
      const liite = tiedosto ? { nimi: tiedosto.name, sisalto: await lueDataUrl(tiedosto) } : undefined;
      await api.tallennaPainekoe(linjaId, {
        ...(liite ? { liite } : {}),
        // ⛔ Tyhjä päivä jätetään POIS eikä lähetetä null:na: skeema (PVM) hylkäsi
        // null:n («Päivä: väärä muoto»), joten painekoetta ei voinut tallentaa
        // ilman päivää. Palvelin lukee puuttuvan samoin kuin tyhjän (23.9.2026,
        // löytyi kun kävely painoi lomaketta).
        tulos, ...(paiva ? { paiva } : {}), raportti: raportti || undefined,
        tarkastaja: tarkastaja || undefined,
        ...(perusteKysytty && peruste.trim() ? { ndt_ohitus_peruste: peruste.trim() } : {}),
      });
      setAuki(false); setPerusteKysytty(false); setPeruste(""); setTiedosto(null);
      lataa();
    } catch (e) {
      // 161: portti ei ole virhe vaan kysymys. Palvelin nimeää NDT:n tilan,
      // ja perustekenttä avautuu vastaamaan siihen.
      if (e instanceof ApiError && e.virhe === "ndt_kesken") setPerusteKysytty(true);
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tk.tallennus_virhe"));
    }
  }

  if (!ladattu) return null;

  return (
    <div className="panel__osa" data-painekoe={rivi ? rivi.tulos : "ei_kirjattu"}>
      {/* Osion otsikko kuten Linjatarkastus ja Laippaliitokset (Toni 25.10.2026: «otsikot on himmeenä»). */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0 }} data-osio-otsikko="">{t("pai.otsikko")}</h3>
        {saaKirjata && !auki && (
          <button className="nappi nappi--kevyt" onClick={() => setAuki(true)}>
            {rivi ? t("pai.muokkaa") : t("pai.kirjaa")}
          </button>
        )}
      </div>

      {/* Neljä eri tilaa, neljä eri sanaa. «Ei kirjattu» ja «tulos ei
          tiedossa» ovat eri asioita: jälkimmäisessä koe on tehty ja
          lähde kertoo sen, muttei tulosta. */}
      {!rivi && <Tilasana laji="ei_mitattu">{t("pai.ei_kirjattu")}</Tilasana>}
      {rivi && (
        <div className="kirjaus__arvot">
          <Tilasana laji={rivi.tulos === "hyvaksytty" ? "kunnossa"
            : rivi.tulos === "hylatty" ? "vika"
            : rivi.tulos === "kesken" ? "varoitus" : "ei_mitattu"}>
            {(() => { const x = TULOKSET.find((r) => r.arvo === rivi.tulos); return x ? t(x.nimi) : rivi.tulos; })()}
          </Tilasana>
          <span className="mono dim">
            {naytaPvm(rivi.paiva) ?? t("pai.ei_paivaa")}
            {rivi.raportti ? ` · ${rivi.raportti}` : ""}
            {rivi.tarkastaja ? ` · ${rivi.tarkastaja}` : ""}
          </span>
        </div>
      )}

      {/* Alkuperäinen lähdeteksti näkyviin sellaisenaan: tuonti ei lukenut
          siitä painetta eikä väliainetta, eikä niitä keksitä myöhemminkään. */}
      {rivi?.huomiot && <p className="huom">{t("pai.lahde")} {rivi.huomiot}</p>}

      {/* 161: kirjattu NDT:n ollessa kesken — sanotaan joka kerta kun rivi
          näytetään, ei vain kirjaushetkellä. Jäädytetty tila kertoo mitä
          silloin tiedettiin. */}
      {rivi?.ndt_ohitus_peruste && (
        <p className="huom" data-painekoe-ohitus={rivi.id}>
          <Tilasana laji="varoitus">{t("pai.ennen_ndt")}</Tilasana>{" "}
          {t("pai.ohitus", { tila: rivi.ndt_tila_kirjattaessa ?? "—", peruste: rivi.ndt_ohitus_peruste })}
          {rivi.ndt_ohitus_kuka ? ` · ${rivi.ndt_ohitus_kuka}` : ""}
          {rivi.ndt_ohitus_hetki ? ` ${naytaPvm(rivi.ndt_ohitus_hetki.slice(0, 10))}` : ""}
        </p>
      )}
      {rivi && !rivi.ndt_ohitus_peruste && rivi.ndt_tila_kirjattaessa
        && /^(luokka_puuttuu|ei_asetettu|luokalle_ei_kirjattu)/.test(rivi.ndt_tila_kirjattaessa) && (
        <p className="huom dim">
          {t("pai.ei_mitattu_kirjattaessa", { tila: rivi.ndt_tila_kirjattaessa })}
        </p>
      )}

      {auki && (
        <div className="allokointi">
          <label>{t("pai.tulos")}
            <select value={tulos} onChange={(e) => setTulos(e.target.value as PainekoeRivi["tulos"])}>
              {TULOKSET.map((r) => <option key={r.arvo} value={r.arvo}>{t(r.nimi)}</option>)}
            </select>
          </label>
          <label>{t("pai.paiva")}<input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} /></label>
          <label>{t("pai.poytakirja_nro")}
            <input value={raportti} size={12} onChange={(e) => setRaportti(e.target.value)} />
          </label>
          <label>{t("pai.tarkastaja")}
            <input value={tarkastaja} size={12} data-kentta="painekoe_tarkastaja" onChange={(e) => setTarkastaja(e.target.value)} />
          </label>
          {/* T396: pöytäkirja mukaan samaan tallennukseen. Olemassa olevalle riville liitteet hoitaa
              alla oleva Liitteet-lista, joten kenttä näkyy vain ensimmäisessä kirjauksessa. */}
          {!rivi && (
            <label data-painekoe-liite="">{t("pai.poytakirja_liite")}
              <input type="file" accept=".pdf,image/*"
                     onChange={(e) => setTiedosto(e.target.files?.[0] ?? null)} />
            </label>
          )}
          {perusteKysytty && (
            <label className="kasvaa" data-painekoe-peruste="">{t("pai.peruste")}
              <input value={peruste} size={40}
                     placeholder={t("pai.peruste_esim")}
                     onChange={(e) => setPeruste(e.target.value)} />
            </label>
          )}
          <button className="nappi nappi--paa" disabled={perusteKysytty && !peruste.trim()}
                  onClick={() => void tallenna()}>
            {perusteKysytty ? t("pai.tallenna_perusteella") : t("ltar.tallenna")}
          </button>
          <button className="nappi nappi--kevyt"
                  onClick={() => { setAuki(false); setPerusteKysytty(false); setPeruste(""); setVirhe(null); }}>
            {t("hl.peruuta")}
          </button>
        </div>
      )}

      {rivi && (
        <Liitteet kohdeTaulu="tarkastus" kohdeId={rivi.id} saaMuokata={saaKirjata}
                  saaPoistaa={saaPoistaaLiitteen} otsikko={t("pai.poytakirja")} muuttui={lataa} />
      )}

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </div>
  );
}
